import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import publicApi from "../../lib/publicApi";
import { formatMYR } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, Minus, ShoppingBag, Truck, MapPin, Ticket, Check } from "lucide-react";

const DELIVERY = [
    { value: "邮寄", label: "邮寄配送 (+RM 10)", fee: 10 },
    { value: "自取", label: "到店自取 (免邮)", fee: 0 },
];

export default function ShopCheckout() {
    const { items, setQty, remove, clear, subtotal } = useCart();
    const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", note: "" });
    const [delivery, setDelivery] = useState("邮寄");
    const [couponInput, setCouponInput] = useState("");
    const [coupon, setCoupon] = useState(null); // {code, discount, free_shipping}
    const [validating, setValidating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const nav = useNavigate();

    const deliveryFee = coupon?.free_shipping ? 0 : (DELIVERY.find((d) => d.value === delivery)?.fee || 0);
    const couponDiscount = coupon?.discount || 0;
    const total = Math.max(0, subtotal - couponDiscount) + deliveryFee;

    const validate = async () => {
        if (!couponInput.trim()) return;
        setValidating(true);
        try {
            const { data } = await publicApi.post("/public/coupons/validate", {
                code: couponInput.trim().toUpperCase(),
                subtotal,
            });
            setCoupon(data);
            toast.success(`优惠码已应用：${data.code}`);
        } catch (err) {
            setCoupon(null);
            toast.error(err.response?.data?.detail || "优惠码无效");
        }
        setValidating(false);
    };

    const removeCoupon = () => {
        setCoupon(null);
        setCouponInput("");
    };

    const submit = async (e) => {
        e.preventDefault();
        if (items.length === 0) return toast.error("购物车为空");
        if (!form.name.trim()) return toast.error("请填写姓名");
        if (!form.phone.trim()) return toast.error("请填写电话");
        if (delivery === "邮寄" && !form.address.trim()) return toast.error("请填写收货地址");
        setSubmitting(true);
        try {
            const payload = {
                customer_name: form.name.trim(),
                customer_phone: form.phone.trim(),
                email: form.email.trim(),
                delivery_method: delivery,
                delivery_address: delivery === "邮寄" ? form.address.trim() : "",
                note: form.note.trim(),
                items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
                coupon_code: coupon?.code || "",
            };
            const { data } = await publicApi.post("/public/orders", payload);
            clear();
            nav(`/shop/thank-you/${data.invoice_no}`, { state: data });
        } catch (err) {
            toast.error(err.response?.data?.detail || "下单失败，请稍后重试");
        } finally {
            setSubmitting(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="text-center py-20" data-testid="shop-checkout-empty">
                <ShoppingBag className="h-16 w-16 mx-auto text-gold-300 mb-4" />
                <h2 className="text-2xl font-bold text-slate-900">购物车是空的</h2>
                <p className="text-sm text-slate-500 mt-2">先去挑几件心仪的首饰吧</p>
                <Button onClick={() => nav("/shop")} className="mt-6 bg-gold-600 hover:bg-gold-700 text-white">去逛逛</Button>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="shop-checkout">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">结算</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card className="p-6 border-gold-200 bg-white/80 backdrop-blur-sm shadow-sm">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600 mb-4">购物车</h2>
                        <div className="space-y-3">
                            {items.map((it) => (
                                <div key={it.product_id} className="flex items-center gap-4 pb-3 border-b border-gold-100 last:border-0 last:pb-0">
                                    <div className="w-16 h-16 bg-gold-50 rounded-md overflow-hidden shrink-0">
                                        {it.image_url ? (
                                            <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gold-300"><ShoppingBag className="h-6 w-6" /></div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-slate-900 truncate">{it.name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{it.sku}</div>
                                        <div className="text-sm font-mono text-gold-700 mt-1">{formatMYR(it.unit_price)}</div>
                                    </div>
                                    <div className="flex items-center gap-1 border border-gold-200 rounded-md bg-white">
                                        <button type="button" onClick={() => setQty(it.product_id, it.quantity - 1)} className="h-8 w-8 flex items-center justify-center text-gold-600 hover:bg-gold-50"><Minus className="h-3 w-3" /></button>
                                        <span className="w-8 text-center font-mono text-sm">{it.quantity}</span>
                                        <button type="button" onClick={() => setQty(it.product_id, it.quantity + 1)} disabled={it.quantity >= it.stock} className="h-8 w-8 flex items-center justify-center text-gold-600 hover:bg-gold-50 disabled:opacity-30"><Plus className="h-3 w-3" /></button>
                                    </div>
                                    <div className="w-24 text-right font-mono font-bold text-slate-900">{formatMYR(it.unit_price * it.quantity)}</div>
                                    <button type="button" onClick={() => remove(it.product_id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            ))}
                        </div>
                    </Card>

                    <form onSubmit={submit} id="checkout-form" className="space-y-4">
                        <Card className="p-6 border-gold-200 bg-white/80 backdrop-blur-sm shadow-sm space-y-4">
                            <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600">联系方式</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label>姓名 *</Label>
                                    <Input data-testid="checkout-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>电话 *</Label>
                                    <Input data-testid="checkout-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+60 12-345 6789" required />
                                </div>
                                <div className="space-y-1.5 md:col-span-2">
                                    <Label>邮箱 (可选)</Label>
                                    <Input type="email" data-testid="checkout-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                                </div>
                            </div>
                        </Card>

                        <Card className="p-6 border-gold-200 bg-white/80 backdrop-blur-sm shadow-sm space-y-4">
                            <h2 className="text-sm font-semibold uppercase tracking-wider text-gold-600 flex items-center gap-2"><Truck className="h-3.5 w-3.5" /> 配送方式</h2>
                            <Select value={delivery} onValueChange={setDelivery}>
                                <SelectTrigger data-testid="checkout-delivery"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {DELIVERY.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            {delivery === "邮寄" && (
                                <div className="space-y-1.5">
                                    <Label className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> 收货地址 *</Label>
                                    <Textarea rows={2} data-testid="checkout-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
                                </div>
                            )}
                            <div className="space-y-1.5">
                                <Label>备注 (可选)</Label>
                                <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="例如：送礼包装" />
                            </div>
                        </Card>
                    </form>
                </div>

                <div className="lg:col-span-1">
                    <Card className="p-6 border-gold-200 bg-white/80 backdrop-blur-sm shadow-sm sticky top-24 space-y-4">
                        <h2 className="text-lg font-semibold text-slate-900">订单摘要</h2>

                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between text-slate-600">
                                <span>商品小计</span>
                                <span className="font-mono">{formatMYR(subtotal)}</span>
                            </div>
                            {couponDiscount > 0 && (
                                <div className="flex justify-between text-red-600">
                                    <span>优惠码 {coupon.code}</span>
                                    <span className="font-mono">- {formatMYR(couponDiscount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-slate-600">
                                <span>邮费</span>
                                <span className="font-mono">{deliveryFee > 0 ? formatMYR(deliveryFee) : <span className="text-gold-700">免费</span>}</span>
                            </div>
                            <div className="pt-3 border-t border-gold-200 flex justify-between items-baseline">
                                <span className="text-sm font-semibold uppercase tracking-wider text-slate-500">合计</span>
                                <span className="text-2xl font-mono font-bold text-gold-700">{formatMYR(total)}</span>
                            </div>
                        </div>

                        {/* Coupon input */}
                        <div className="pt-2 border-t border-gold-100">
                            <Label className="flex items-center gap-1.5 mb-2"><Ticket className="h-3.5 w-3.5 text-gold-600" /> 优惠码</Label>
                            {coupon ? (
                                <div className="flex items-center justify-between p-2 rounded bg-gold-100/60 border border-gold-200">
                                    <div className="flex items-center gap-2">
                                        <Check className="h-4 w-4 text-gold-700" />
                                        <code className="font-mono font-bold text-gold-800">{coupon.code}</code>
                                    </div>
                                    <button type="button" onClick={removeCoupon} className="text-xs text-red-600 hover:underline">移除</button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <Input
                                        data-testid="checkout-coupon-input"
                                        placeholder="输入优惠码"
                                        value={couponInput}
                                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                                        className="font-mono uppercase"
                                    />
                                    <Button type="button" onClick={validate} disabled={validating} variant="outline" data-testid="checkout-apply-coupon-btn">
                                        {validating ? "..." : "使用"}
                                    </Button>
                                </div>
                            )}
                        </div>

                        <Button
                            type="submit"
                            form="checkout-form"
                            data-testid="checkout-submit-btn"
                            disabled={submitting}
                            className="w-full h-11 bg-gold-600 hover:bg-gold-700 text-white font-semibold"
                        >
                            {submitting ? "下单中..." : "确认下单"}
                        </Button>
                        <p className="text-xs text-slate-500 text-center">
                            下单后订单状态为「待处理」，我们会尽快与您确认付款方式
                        </p>
                    </Card>
                </div>
            </div>
        </div>
    );
}
